// Apolo IDE — Merge Queue lane (sprint honest-green-merge-queue, P4).
//
// Glass Box surface for the Honest-Green Merge Queue. Renders the
// buildQueueView DTO (apolo-api/src/lib/mergeQueue.js) in the SAME knob/rail/
// line idiom as the task DAG (rx-tp-*). VERDICT-ONLY: it shows each PR's
// position, prospective-merge SHA, oracle verdict, and a stale badge — and an
// "Abrir PR" affordance that opens the PR for a HUMAN to land. It NEVER lands
// a PR itself (LAW: nothing auto-merges).
//
// Purely presentational: takes the view DTO as a prop, dispatches
// apolo:openPR on the land affordance. No fetch / no store inside (the parent
// reads code.mergeQueue and passes it down) so it's trivially testable.

import { Check, GitPullRequest, Loader2, AlertTriangle, ExternalLink } from 'lucide-react';

const STATUS_STATE = {
  landable: 'green',
  verifying: 'in_progress',
  rebasing: 'in_progress',
  failed: 'failed',
  stale: 'pending',
  queued: 'pending',
  landed: 'green',
};

function Knob({ status }) {
  const st = STATUS_STATE[status] || 'pending';
  if (st === 'green') {
    return (
      <span className="rx-tp-knob">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
      </span>
    );
  }
  if (st === 'in_progress') {
    return <span className="rx-tp-knob"><span className="rx-tp-spin" aria-hidden="true" /></span>;
  }
  return <span className="rx-tp-knob" aria-hidden="true" />;
}

function shortSha(sha) {
  return typeof sha === 'string' && sha.length > 7 ? sha.slice(0, 7) : (sha || '');
}

export default function MergeQueueLane({ view, sessionId }) {
  if (!view || !Array.isArray(view.entries) || view.entries.length === 0) return null;

  const openPR = (prNumber) => {
    if (typeof window === 'undefined') return;
    // VERDICT-ONLY: open the PR for a human to land. NEVER auto-merge.
    window.dispatchEvent(new CustomEvent('apolo:openPR', { detail: { sessionId, prNumber } }));
  };

  return (
    <section className="rx-tp-mq" aria-label="Merge Queue" data-testid="merge-queue-lane">
      <div className="rx-tp-divider" />
      <div className="rx-tp-sec" data-testid="mq-heading">
        Merge Queue · honest-green vs main
        {view.mainSha ? ` @ ${shortSha(view.mainSha)}` : ''}
      </div>

      <ol
        className="rx-tp-dag"
        aria-label="PRs na fila de merge"
        style={{ margin: 0, padding: 0, listStyle: 'none' }}
        data-testid="mq-list"
      >
        {view.entries.map((e, i) => {
          const last = i === view.entries.length - 1;
          return (
            <li
              key={e.prNumber}
              className="rx-tp-node"
              data-state={STATUS_STATE[e.status] || 'pending'}
              data-testid={`mq-entry-${e.prNumber}`}
              data-status={e.status}
            >
              <div className="rx-tp-rail">
                <Knob status={e.status} />
                {!last && <div className="rx-tp-vline" />}
              </div>
              <div className="rx-tp-ncard">
                <div className="rx-tp-ntitle">
                  <GitPullRequest size={12} aria-hidden="true" style={{ color: 'var(--rx-tx3)' }} />
                  <span className="rx-tp-id">#{e.prNumber}</span>
                  {e.position != null && (
                    <span className="rx-tp-id" data-testid={`mq-pos-${e.prNumber}`}>pos {e.position}</span>
                  )}
                  {e.fastTrack && <span className="rx-badge rx-badge--new" style={{ marginLeft: 4 }}>fast-track</span>}
                </div>
                <div className="rx-tp-ndep">
                  {e.prospectiveMergeSha
                    ? <><span className="rx-tp-b">prospective-merge:</span> {shortSha(e.prospectiveMergeSha)}</>
                    : <span style={{ color: 'var(--rx-tx4)' }}>aguardando rebase no HEAD</span>}
                </div>
                <div
                  className="rx-tp-oracle"
                  data-pend={e.landable ? 'false' : 'true'}
                  data-testid={`mq-verdict-${e.prNumber}`}
                >
                  {e.landable && <Check size={10} aria-hidden="true" />}
                  {e.landable && 'landable · green-against-main'}
                  {!e.landable && e.status === 'failed' && (<><AlertTriangle size={10} aria-hidden="true" /> failed vs merge</>)}
                  {!e.landable && e.status === 'verifying' && (<><Loader2 size={10} className="animate-spin" aria-hidden="true" /> verifying…</>)}
                  {!e.landable && e.stale && 'stale · re-verify vs new HEAD'}
                  {!e.landable && (e.status === 'queued') && 'queued'}
                </div>
                {e.landable && (
                  <button
                    type="button"
                    className="rx-btn"
                    data-testid={`mq-open-${e.prNumber}`}
                    onClick={() => openPR(e.prNumber)}
                    style={{ marginTop: 6, padding: '4px 10px', fontSize: '11px' }}
                  >
                    <ExternalLink size={10} aria-hidden="true" />
                    Abrir PR pra dar merge
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      <p className="rx-tp-empty" style={{ padding: '8px 4px 0', textAlign: 'left' }} data-testid="mq-law">
        A fila NUNCA faz merge sozinha — só verifica o estado mergeado e mostra o veredito. Você landa.
      </p>
    </section>
  );
}
